import { useState } from 'react';
import { Alert, Button, Group, Modal, PasswordInput, Stack, Text } from '@mantine/core';
import { useMutation } from '@tanstack/react-query';
import { notifications } from '@mantine/notifications';
import { api, loadAuth } from '../api/client';
import { describeApiError } from '../api/errors';
import { useAuth } from '../auth/AuthContext';

/**
 * Смена своего пароля.
 *
 * До неё пароль сотруднику заводил администратор через раздел
 * «Сотрудники» — то есть он знал пароли всех, а замерщик не мог сменить
 * свой, даже если тот утёк. Демо-учётки с общим паролем жили ровно
 * поэтому: сменить их было некому и нечем.
 *
 * Текущий пароль спрашивается, хотя человек уже вошёл: планшет один на
 * несколько замерщиков, и его оставляют разблокированным. Без проверки
 * любой, кто взял его в руки, запирает владельца снаружи.
 *
 * После смены сервер гасит остальные сессии и говорит сколько. Это не
 * отчётность ради отчётности: если человек менял пароль из-за утечки,
 * ему важно увидеть, что чужой вход отвалился.
 */

export function ChangePasswordModal({ opened, onClose }: { opened: boolean; onClose: () => void }) {
  const [current, setCurrent] = useState('');
  const [next, setNext] = useState('');
  const [repeat, setRepeat] = useState('');
  const [error, setError] = useState<string | null>(null);
  const { clearWeakPassword } = useAuth();

  const reset = () => {
    setCurrent('');
    setNext('');
    setRepeat('');
    setError(null);
  };

  const save = useMutation({
    mutationFn: () =>
      api.post<{ revokedSessions: number }>('/auth/change-password', {
        currentPassword: current,
        newPassword: next,
        // Текущую сессию сервер оставит живой: менять пароль и тут же
        // оказаться на экране входа — раздражает и выглядит как сбой.
        refreshToken: loadAuth()?.refreshToken,
      }),
    onSuccess: (data) => {
      notifications.show({
        color: 'teal',
        title: 'Пароль изменён',
        message:
          data.revokedSessions > 0
            ? `Другие входы отключены: ${data.revokedSessions}. Там придётся войти заново.`
            : 'Других входов не было.',
      });
      // Красная полоса «ваш пароль известен посторонним» должна уйти
      // сразу: она висит до конца сессии, и оставленная после смены она
      // учит не читать предупреждения
      clearWeakPassword();
      reset();
      onClose();
    },
    onError: (e) => setError(describeApiError(e, 'Не удалось сменить пароль')),
  });

  const mismatch = repeat.length > 0 && next !== repeat;
  const tooShort = next.length > 0 && next.length < 8;
  const canSave = current.length > 0 && next.length >= 8 && next === repeat;

  return (
    <Modal
      opened={opened}
      onClose={() => {
        reset();
        onClose();
      }}
      title="Смена пароля"
      centered
    >
      <Stack gap="sm">
        <PasswordInput
          label="Текущий пароль"
          value={current}
          onChange={(e) => setCurrent(e.currentTarget.value)}
          autoComplete="current-password"
          data-autofocus
        />

        <PasswordInput
          label="Новый пароль"
          description="Не короче восьми знаков"
          value={next}
          onChange={(e) => setNext(e.currentTarget.value)}
          error={tooShort ? 'Слишком короткий' : null}
          autoComplete="new-password"
        />

        <PasswordInput
          label="Ещё раз"
          value={repeat}
          onChange={(e) => setRepeat(e.currentTarget.value)}
          error={mismatch ? 'Пароли не совпадают' : null}
          autoComplete="new-password"
        />

        {error && <Alert color="red">{error}</Alert>}

        <Text size="xs" c="dimmed">
          Остальные входы под вашей учётной записью будут отключены — на других устройствах придётся
          войти заново.
        </Text>

        <Group justify="flex-end" gap="xs" mt="xs">
          <Button
            variant="subtle"
            onClick={() => {
              reset();
              onClose();
            }}
          >
            Отмена
          </Button>
          <Button disabled={!canSave} loading={save.isPending} onClick={() => save.mutate()}>
            Сменить
          </Button>
        </Group>
      </Stack>
    </Modal>
  );
}
